import React, { Fragment, useEffect, useState } from "react";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css";
import { useHistory, Link } from "react-router-dom";
import api from "../../../api";
import ContainerFitLoader from "../../../components/loader/container-fit-loader";
import {
  ILeadEvent,
  capitalizeFirstLetter,
  useHasPermission,
  AppPermissions,
  Customer,
  Lead,
  getInvalidCustomerFieldsEnquiry,
} from "../../../core";
import { IBigCalendarEvent } from "../../../core/components/calendar/big-calendar-component";
import { useConfirmationDialog } from "../../../core/components/confirm-popup/confirm-popup";
import { useCalendarContext } from "../../../core/hooks/useCalendarContext";
import { useLeadActivities } from "../../../core/hooks/useLeadActivities";
import { DateFormats, formatDate } from "../../../core/utils/date-utils";
import { ActivityModel } from "../../leads/components/activities/add-activity";
import { EventCancelForm } from "./event-cancel-form";

import { RouteKeys } from "../../routes/route-keys";
import { UserAvatar } from "../../../components/render-file/UserAvatar";
import { useMasterData } from "../../../core/hooks/useMasterData";
import { EventDoneForm } from "./event-done-form";

interface IToolTipContentProps {
  event: IBigCalendarEvent;
  onClose: any;
  customerId?: string;
  leadId?: string;
  setCustomerModal?: (id: string, eventId: string, leadDetails?: Lead) => void;
  addActivity?:(data:any) => void,  
}

export const EventPopupContent: React.FunctionComponent<IToolTipContentProps> = (
  props
) => {
  const masterData = useMasterData(api.settings);
  const history = useHistory();
  const event = props.event;
  const { getEventDetails, createEventUpdateFormData } = useLeadActivities(
    api.lead
  );
  const { getConfirmation } = useConfirmationDialog();
  const [done, setDone] = useState(event?.appointment?.isCompleted ?? false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const canModifyActivityStatus = useHasPermission(
    AppPermissions.ModifyActivityStatus
  );

  const [showMarkAsDoneForm, setShowMarkAsDoneForm] = React.useState<{
    isOpen?: boolean;
    event?: IBigCalendarEvent;
  }>({});
  const canUpdateLeadActivity = useHasPermission(
    AppPermissions.UpdateLeadActivity
  );

  const {
    setCurrentActivity,
    eventMarkAsDone,
    isLoading,
    getCalendarEvents,
  } = useCalendarContext();
  useEffect(() => {
    setDone(event.appointment.isCompleted ?? false);
  }, [event.appointment.isCompleted]);
  const onPressEdit = async () => {
    getEventDetails(event?.id, (eventDetails: ILeadEvent) => {
      const activityModel: ActivityModel = createEventUpdateFormData(
        eventDetails
      );
      setCurrentActivity(activityModel);
    });
    props.onClose();
  };
  const showConfirm = () => {
    return getConfirmation({
      title: "Would You Like Mark The Event As Done?",
    });
  };
  const handleMarkAsDone = async (mileageIn?: string, mileageOut?: string) => {
    if (!!invalidCustomerFieldsForEnquiry?.length) {
      setShowConfirmModal(true);
    } else {
      eventMarkAsDone(
        event.id,
        () => {
          getCalendarEvents();
          if(props.addActivity){
            props.addActivity(event)
          }
          props.onClose();
        },
        mileageIn,
        mileageOut
      );
    }
  };

  const [expanded, setExpanded] = useState(false);

  const [shortNote, setShortNote] = useState("");
  const [customer, setCustomer] = useState<Customer>();
  const [currentLead, setCurrentLead] = useState<Lead>();
  useEffect(() => {
    if ((event?.appointment?.note ?? "").length > 100) {
      setShortNote((event?.appointment?.note ?? "").substring(0, 99));
    } else {
      setExpanded(true);
    }
  });

  React.useEffect(() => {
    if (props.customerId) {
      getCustomerDetails();
      getLead();
    }
  }, [props.customerId]);

  const getCustomerDetails = async () => {
    const res = await api.customer.getDetails(props.customerId || "");
    setCustomer(res);
  };

  const getLead = async () => {
    const res = await api.lead.getLead(event?.appointment?.leadId || "");
    setCurrentLead(res?.data);
  };

  const invalidCustomerFieldsForEnquiry =
    !currentLead?.isDeal && customer
      ? getInvalidCustomerFieldsEnquiry(
        customer,
        masterData?.data?.leadStages
          ?.find((x) => x.code === "enquiry")
          ?.entity?.find((c) => c.key === "Customer")!
      )
      : [];

  const on360Click = (id: string) => {
    history.push(`${RouteKeys.Lead360View}/${id}`);
  };

  const renderNoEventInformation = () => {
    return showConfirmModal ? (
      <>
        <div
          className="modal fade show in"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body ">
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <p>
                        Some information is missing in the customer profile for
                        converting Lead to Enquiry. Please update the customer
                        information in order to proceed.
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-auto mr-auto">
                      <button
                        type="button"
                        className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
                        onClick={() => {
                          setShowConfirmModal(false);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="col-auto">
                      <button
                        type="button"
                        className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
                        onClick={() => {
                          props.setCustomerModal?.(
                            event?.appointment?.customer?._id || "",
                            event?.id || "",
                            currentLead
                          );
                          setShowConfirmModal(false);
                          props.onClose();
                        }}
                      >
                        Update Profile
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    ) : (
      <></>
    );
  };
  const onMarkAsDone = (currentEvent: IBigCalendarEvent) => {
    if (!!invalidCustomerFieldsForEnquiry?.length) {
      setShowConfirmModal(true);
    } else {
      if (currentEvent.activityType === "scheduleATestDrive") {
        setShowMarkAsDoneForm({
          isOpen: true,
          event: currentEvent,
        });
      } else {
        handleMarkAsDone();
      }
    }
  };

  return (
    <Fragment>
      <Fragment>
        <div className="calendar-info-popover">
          {!showCancelForm && (
            <Fragment>
              <div className="container-fluid py-4 popover-style position-relative">
                <div className="row mb-4">
                  <div className="col-auto pr-1">
                    <i
                      className={`${event.displayDetails.icon} text-gray-700 font-xxl popover-icon`}
                    ></i>
                  </div>
                  <div className="col">
                    <h5 className="font-weight-semibold mb-2">
                      <Link
                        to={`${RouteKeys.Leads}/${event?.appointment?.leadId}`}
                        className="Logo__link"
                      >
                        {capitalizeFirstLetter(event.title)}
                      </Link>
                    </h5>
                    <div className="font-sm text-gray-500 font-weight-semibold">
                      {formatDate(event.start, DateFormats.TimeOnlyFormat)} -{" "}
                      {formatDate(event.end, DateFormats.TimeOnlyFormat)}{" "}
                      {formatDate(event.start, "dddd")}
                      {", "}
                      {formatDate(event.start, "MMMM DD")}
                    </div>
                  </div>
                </div>
                {/* <div className="row mb-3">
                  <div className="col-auto pr-1">
                    <i
                      className={
                        event.displayDetails.icon +
                        ' text-gray-400 popover-icon'
                      }
                    ></i>
                  </div>
                  <div className="col">
                    <h6 className="font-weight-semibold mb-0">
                      {event.displayDetails.text}
                    </h6>
                  </div>
                </div> */}
                {event?.appointment?.customer?.fullName != null && (
                  <div
                    className="row mb-3 cursor-pointer"
                    onClick={() => {
                      on360Click(event?.appointment?.customer?._id || "");
                    }}
                  >
                    <div className="col-auto pr-1">
                      <i className="icon-user-rounded text-gray-400 popover-icon"></i>
                    </div>
                    <div className="col">
                      <h6 className="font-weight-semibold mb-0">
                        {event?.appointment?.customer?.fullName}
                      </h6>
                    </div>
                    <div className="col-auto ">
                      <Link
                        to={`${RouteKeys.Lead360View}/${event?.appointment?.customer?._id}`}
                        className="Logo__link"
                      >
                        <i className="icon-view"></i>
                      </Link>
                    </div>
                  </div>
                )}

                {event?.appointment?.vehicle?.name != null && (
                  <div className="row mb-3">
                    <div className="col-auto pr-1">
                      <i className="icon-drive text-gray-400 popover-icon"></i>
                    </div>
                    <div className="col">
                      <h6 className="font-weight-semibold mb-0">
                        {event?.appointment?.vehicle?.name}
                      </h6>
                    </div>
                  </div>
                )}

                {/* <div className="row mb-3">
                  <div className="col-auto pr-1">
                    <i className="icon-dollar text-gray-400 popover-icon"></i>
                  </div>
                  <div className="col">
                    <h6 className="font-weight-semibold mb-0">
                      <div className="tag tag--deal mr-2">Deal</div>
                      2020 Chevrolet Spark
                      </h6>
                  </div>
                </div> */}

                {event?.appointment?.assignedPerson?.name != null && (
                  <div className="row mb-3 align-items-center">
                    <div className="col-auto pr-1">
                      <i className="icon-agent text-gray-400 popover-icon"></i>
                    </div>
                    <div className="col">
                      <div className="user d-inline-flex align-items-center">
                        <div className="user-img">
                          <div className="avatar-progress avatar-progress--xs position-relative cursor-pointer">
                            <svg>
                              <circle
                                r="12"
                                cx="50%"
                                cy="50%"
                                className="default"
                              ></circle>
                              <circle
                                r="12"
                                cx="50%"
                                cy="50%"
                                className="progress"
                                stroke-dasharray="117"
                                stroke-dashoffset="75"
                              ></circle>
                            </svg>
                            <UserAvatar
                              id={event?.appointment?.assignedPerson?._id}
                              className="position-absolute left-0 right-0 top-0 bottom-0 m-auto rounded-circle"
                              alt={event?.appointment?.assignedPerson?.name}
                              title={event?.appointment?.assignedPerson?.name}
                              imageUrl={
                                event?.appointment?.assignedPerson?.avatar
                                  ?.location
                              }
                              image={event?.appointment?.assignedPerson?.avatar}
                            ></UserAvatar>
                          </div>
                        </div>
                        <div className="user-name ml-2 font-normal font-weight-semibold">
                          {event?.appointment?.assignedPerson?.name}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {event?.appointment?.note != null && (
                  <div className="row">
                    <div className="col-auto pr-1">
                      <i className="icon-detail text-gray-400 popover-icon"></i>
                    </div>
                    <div className="col">
                      <div className="font-normal mb-2 font-weight-semibold">
                        {expanded
                          ? event?.appointment?.note
                          : shortNote + "..."}
                        {shortNote.length > 0 && (
                          <span
                            className="text-primary cursor-pointer"
                            onClick={() => {
                              setExpanded(!expanded);
                            }}
                          >
                            {!expanded ? "Read more" : " Read less"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {event?.appointment?.bookedBy?.name != null && (
                  <div className="row mb-3">
                    <div className="col-auto pr-1">
                      <i className="icon-user-rounded text-gray-400 popover-icon"></i>
                    </div>
                    <div className="col">
                      <h6 className="font-weight-semibold mb-0">
                        <span className="">Created By : </span>
                        {event?.appointment?.bookedBy?.name}
                      </h6>
                    </div>
                  </div>
                )}
                {event?.appointment?.companyName && (
                  <div className="row mb-3">
                    <div className="col-auto pr-1">
                      <i className="icon-user-rounded text-gray-400 popover-icon"></i>
                    </div>
                    <div className="col">
                      <h6 className="font-weight-semibold mb-0">
                        <span className="">Company Name : </span>
                        <Link to={`${RouteKeys.Company360View}/${(event?.appointment?.leadDetails?.company?._id || event?.appointment?.companyId)}?tab=activities`}>
                          {event?.appointment?.companyName}
                        </Link>
                      </h6>
                    </div>
                  </div>
                )}
                {!!event?.appointment?.companyPeople?.length && (
                  <div className="row mb-3">
                    <div className="col-auto pr-1">
                      <i className="icon-user-rounded text-gray-400 popover-icon"></i>
                    </div>
                    <div className="col">
                      <h6 className="font-weight-semibold mb-0">
                        <span className="">People : </span>
                        {event?.appointment?.leadDetails?.company?._id ?
                          <Link to={`${RouteKeys.Company360View}/${event?.appointment?.leadDetails?.company?._id}?tab=activities`}>
                            {event?.appointment?.companyPeople?.map((item: any) => {
                              if (item?.designation) {
                                return `${(item?.fullName || item?.firstName)} (${item.designation})`
                              } else {
                                return `${(item?.fullName || item?.firstName)}`
                              }
                            })?.join(', ')}
                          </Link> :
                          event?.appointment?.companyPeople?.map((item: any) => {
                            if (item?.designation) {
                              return `${(item?.fullName || item?.firstName)} (${item.designation})`
                            } else {
                              return `${(item?.fullName || item?.firstName)}`
                            }
                          })?.join(', ')
                        }
                      </h6>
                    </div>
                  </div>
                )}
                {event?.appointment?.address && (
                  <div className="row">
                    <div className="col-auto pr-1">
                      <i className="icon-location-filled text-gray-400 popover-icon"></i>
                    </div>
                    <div className="col">
                      <div className="font-xs text-gray-500 mb-2">
                        {/* <div className="font-weight-semibold text-gray-900">
                          Office
                        </div> */}
                        {event?.appointment?.address?.street && (
                          <>{event?.appointment?.address?.street} </>
                        )}
                        {event?.appointment?.address?.city && (
                          <>{event?.appointment?.address?.city} </>
                        )}
                        {event?.appointment?.address?.district && (
                          <>{event?.appointment?.address?.district} </>
                        )}
                        {event?.appointment?.address?.zip && (
                          <>P.O Box {event?.appointment?.address?.zip} </>
                        )}
                        {event?.appointment?.address?.country && (
                          <>{event?.appointment?.address?.country} </>
                        )}
                      </div>
                      {/* <div className="d-inline-flex align-items-center font-xs text-primary">
                        <i className="icon-direction mr-1"></i>Direction
                      </div> */}
                    </div>
                  </div>
                )}
              </div>
              {(canModifyActivityStatus || canUpdateLeadActivity) &&
                event?.appointment?.status !== "completed" && (
                  <div className="container-fluid border-top py-2">
                    <div className="row align-items-center">
                      <div className="col-auto">
                        {event?.appointment?.status !== "completed" &&
                          canModifyActivityStatus && (
                            <>
                              <input
                                type="checkbox"
                                name="markcancel"
                                id="markcancel"
                              />
                              <label
                                onClick={() => {
                                  setShowCancelForm(true);
                                }}
                                htmlFor="markcancel"
                                className="font-xs font-weight-semibold"
                              >
                                Mark as Cancelled
                              </label>
                            </>
                          )}
                      </div>
                      <div className="col-auto ml-auto">
                        {event?.appointment?.status !== "completed" &&
                          canModifyActivityStatus &&
                          (
                            <>
                              <input
                                checked={done}
                                type="checkbox"
                                name="markdone"
                                id="markdone"
                              />
                              <label
                                onClick={() => {
                                  if (!done) {
                                    onMarkAsDone(event);
                                  }
                                }}
                                htmlFor="markdone"
                                className="font-xs font-weight-semibold"
                              >
                                Mark as Done
                              </label>
                            </>
                          )}
                      </div>
                      <div className="col-auto">
                        {event?.appointment?.status !== "completed" &&
                          canUpdateLeadActivity && (
                            <button
                              onClick={() => {
                                props.onClose();
                                onPressEdit();
                              }}
                              className="btn border p-2 font-normal font-weight-semibold"
                            >
                              <i className="icon-edit px-1"></i>
                            </button>
                          )}
                      </div>
                    </div>
                  </div>
                )}
            </Fragment>
          )}
          {showCancelForm && (
            <EventCancelForm
              eventId={event.id}
              onSuccess={() => {
                getCalendarEvents();
                if(props.addActivity){
                  props.addActivity(event)
                }
                props.onClose();
              }}
              onCancel={() => {
                setShowCancelForm(false);
              }}
            ></EventCancelForm>
          )}
          {showMarkAsDoneForm.isOpen && (
            <EventDoneForm
              eventId={event?.id || ""}
              onSuccess={() => {
                setShowMarkAsDoneForm({});
              }}
              onCancel={() => {
                setShowMarkAsDoneForm({});
              }}
              markAsDone={handleMarkAsDone}
            ></EventDoneForm>
          )}
        </div>

        {renderNoEventInformation()}
      </Fragment>

      {isLoading && <ContainerFitLoader></ContainerFitLoader>}
    </Fragment>
  );
};
